import { doc, updateDoc, serverTimestamp, Timestamp } from "firebase/firestore";
import { db, functions } from "../firebaseConfig";
import { httpsCallable } from "firebase/functions";

/**
 * Activate tester mode for a user.
 */
export async function activateTesterMode(clientId, adminEmail) {
  const userRef = doc(db, "users", clientId);
  await updateDoc(userRef, {
    testerMode: true,
    subRole: "beta-tester",
    testerActivatedAt: serverTimestamp(),
    testerActivatedBy: adminEmail,
    testerDeactivatedAt: null,
    testerDeactivatedReason: null,
    testerAccessRevoked: false,
    testerAccessRevokedAt: null,
    testerLastActiveAt: serverTimestamp(),
    testerInactivityWarnings: { day15: false, day23: false, day27: false, day30: false },
    testerNda: { signed: false, declined: false },
  });
}

/**
 * Deactivate tester mode for a user.
 */
export async function deactivateTesterMode(clientId, reason = "admin") {
  const userRef = doc(db, "users", clientId);
  await updateDoc(userRef, {
    testerMode: false,
    subRole: null,
    testerDeactivatedAt: serverTimestamp(),
    testerDeactivatedReason: reason,
  });
}

/**
 * Record that a tester declined the NDA.
 */
export async function recordTesterNDADecline(uid) {
  const userRef = doc(db, "users", uid);
  await updateDoc(userRef, {
    "testerNda.declined": true,
    "testerNda.declinedAt": serverTimestamp(),
    "testerNda.signed": false,
    testerMode: false,
    subRole: null,
    testerDeactivatedAt: serverTimestamp(),
    testerDeactivatedReason: "nda_declined",
  });
}

/**
 * Increment tester feedback count. On first feedback, grant premium access.
 */
export async function incrementTesterFeedback(uid) {
  // We need to read current count, but to keep it simple we use setDoc merge
  // The caller (useTesterFeedback) should pass the current count
  // For atomicity, we'll just set fields — the hook handles the count
}

/**
 * Grant tester premium and set first feedback timestamp.
 * Called by useTesterFeedback on first submission.
 */
export async function grantTesterPremium(uid, currentCount) {
  const userRef = doc(db, "users", uid);
  const newCount = currentCount + 1;

  const updates = {
    testerFeedbackCount: newCount,
    testerLastActiveAt: serverTimestamp(),
  };

  // First feedback — grant premium automatically
  if (currentCount === 0) {
    // Premium expires March 1, 2027
    const expiresAt = new Date("2027-03-01T00:00:00Z");
    updates.testerFirstFeedbackAt = serverTimestamp();
    updates.testerPremiumGrantedAt = serverTimestamp();
    updates.testerPremiumExpiresAt = Timestamp.fromDate(expiresAt);
    updates.testerUpgradeAccepted = true;
    updates.testerUpgradeAcceptedAt = serverTimestamp();
  }

  await updateDoc(userRef, updates);
}

/**
 * Send tester welcome email via Mailgun Cloud Function.
 * Sends through resendEmailCallable so it's properly delivered and logged to systemEmails.
 */
export async function sendTesterWelcomeEmail(userDoc) {
  const { testerWelcomeEmailTemplate } = await import("../emails/testerWelcomeEmailTemplate");
  const html = testerWelcomeEmailTemplate(userDoc.fullName || userDoc.name || "Tester");

  const sendEmailFn = httpsCallable(functions, "resendEmailCallable");
  await sendEmailFn({
    toUid: userDoc.id,
    email: {
      subject: "You've Been Selected as a Beta Tester for TheScope180!",
      html,
      type: "Tester Approval",
    },
  });
}

/**
 * Send tester inactivity reminder via Mailgun Cloud Function.
 */
export async function sendTesterInactivityReminder(userDoc, dayNumber) {
  const { testerInactivityEmailTemplate } = await import("../emails/testerInactivityEmailTemplate");
  const html = testerInactivityEmailTemplate(userDoc.fullName || userDoc.name || "Tester", dayNumber);

  const subjects = {
    15: "We Miss You! Check Out What's New on TheScope180",
    23: "7 Days Left Before Your Tester Access Expires",
    27: "3 Days Left! Don't Lose Your Beta Tester Access",
    30: "Your Beta Tester Access Has Been Revoked",
  };

  const sendEmailFn = httpsCallable(functions, "resendEmailCallable");
  await sendEmailFn({
    toUid: userDoc.uid || userDoc.id,
    email: {
      subject: subjects[dayNumber] || "TheScope180 Beta Tester Update",
      html,
      type: "Tester Inactivity Reminder",
    },
  });

  // Update warning flag
  const userRef = doc(db, "users", userDoc.uid || userDoc.id);
  await updateDoc(userRef, {
    [`testerInactivityWarnings.day${dayNumber}`]: true,
  });
}

/**
 * Send deadline reminder to a tester who hasn't submitted feedback.
 * Testing window closes Feb 28, 2026.
 * Supports both user-doc (toUid) and application-doc (toEmail) payloads.
 */
export async function sendTesterDeadlineReminder(userDoc) {
  const { testerDeadlineReminderTemplate } = await import("../emails/testerDeadlineReminderTemplate");
  const html = testerDeadlineReminderTemplate(userDoc.fullName || userDoc.name || "Tester");

  const sendEmailFn = httpsCallable(functions, "resendEmailCallable");

  // If we have a uid (from users collection), use toUid; otherwise fall back to toEmail (application doc)
  const uid = userDoc.uid || userDoc.existingUserId;
  const payload = uid
    ? { toUid: uid, email: { subject: "Reminder: Testing Window Closes February 28 — Don't Miss Your Benefits!", html, type: "Tester Deadline Reminder" } }
    : { toEmail: userDoc.email, email: { subject: "Reminder: Testing Window Closes February 28 — Don't Miss Your Benefits!", html, type: "Tester Deadline Reminder" } };

  await sendEmailFn(payload);
}

/**
 * Revoke tester access after 30-day inactivity.
 */
export async function revokeTesterAccess(clientId) {
  const userRef = doc(db, "users", clientId);
  await updateDoc(userRef, {
    testerAccessRevoked: true,
    testerAccessRevokedAt: serverTimestamp(),
    testerMode: false,
    subRole: null,
    testerDeactivatedAt: serverTimestamp(),
    testerDeactivatedReason: "inactivity",
  });
}
